import axios from "axios";
import {
  enqueueImpactReportPdfConversion,
  fetchImpactReportPdfConversionContent,
  fetchImpactReportPdfConversionStatus,
} from "@/api/impactReportPdf";
import { resolveImpactReportPdfErrorMessage } from "@/components/impactReport/impact-report-error";
import {
  IMPACT_REPORT_PDF_DOWNLOAD_DEADLINE_MS,
  IMPACT_REPORT_PDF_FALLBACK_FILENAME_STEM,
  IMPACT_REPORT_PDF_POLL_INTERVAL_MS,
  IMPACT_REPORT_PDF_POLL_MAX_CONSECUTIVE_FAILURES,
  IMPACT_REPORT_PDF_SUGGESTED_FILENAME_MAX_LENGTH,
} from "@/constants/impactReport";
import type {
  ImpactReportPdfDownloadFailure,
  ImpactReportPdfDownloadSnapshot,
  ImpactReportPdfDownloadSource,
  PdfConversionJob,
} from "@/interfaces/impactReportPdf";
import { downloadBlob } from "@/lib/csv";
import { describeImpactReportError } from "@/utils/impact-report-helper";

/**
 * ── The impact report's PDF download, end to end ───────────────────────────────────────────────
 *
 * `downloadImpactReportPdf` is the ONE function both affordances call — the control bar's control
 * and the closing page's button. Neither owns a second copy of the transport, the serialisation or
 * the filename logic, and `api/impactReportPdf.ts` is this module's only route to the API.
 *
 * ── SHARED STATE, NOT PER-COMPONENT STATE ───────────────────────────────────────────────────────
 * A queued render can take minutes — long enough for the user to reach for the OTHER affordance.
 * A module-level store, read via `useSyncExternalStore`, makes both go busy together and keeps a
 * second job from starting while the first is in flight. It does not live in
 * `ImpactReportContext`: that provider holds state and fetches nothing, and a download is a fetch.
 *
 * ── ABANDONING A DOWNLOAD ───────────────────────────────────────────────────────────────────────
 * When the last subscriber unmounts (the user has left `/impact`), the in-flight download is
 * aborted. The converter keeps rendering regardless; there is simply no UI left to report to.
 */

/**
 * A failure this module has ALREADY classified, carried through `throw` so the catch below reads
 * it as data rather than re-deriving it from a message. Local: nothing else throws or catches it.
 */
class ImpactReportPdfDownloadError extends Error {
  readonly failure: ImpactReportPdfDownloadFailure;

  constructor(failure: ImpactReportPdfDownloadFailure, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "ImpactReportPdfDownloadError";
    this.failure = failure;
  }
}

let snapshot: ImpactReportPdfDownloadSnapshot = { status: "idle", errorMessage: null };
const listeners = new Set<() => void>();
let activeController: AbortController | null = null;

function setSnapshot(next: ImpactReportPdfDownloadSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore`'s snapshot getter. */
export function getImpactReportPdfDownloadSnapshot(): ImpactReportPdfDownloadSnapshot {
  return snapshot;
}

/** `useSyncExternalStore`'s subscribe function. The last unsubscribe abandons the download. */
export function subscribeImpactReportPdfDownload(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size !== 0) return;
    activeController?.abort();
    // An error outliving every affordance that could show it would resurface on the NEXT mount,
    // minutes later and with no user action. Nobody is left to notify, so no `setSnapshot`.
    snapshot = { status: "idle", errorMessage: null };
  };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const timeoutId = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timeoutId);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

function isTerminalJob(job: PdfConversionJob): boolean {
  return job.status === "completed" || job.status === "failed";
}

/**
 * A status-poll failure that no later poll can fix: the job is gone (404), the handle itself was
 * refused (400), or the download was abandoned or hit its deadline. Everything else — any 5xx, a
 * 429, no response — is worth another poll.
 *
 * 503 included: Platform's only 503 on these routes is its own missing converter config, which
 * the enqueue already passed, so one mid-poll is a proxy or deploy blip. A real one still ends at
 * the consecutive-failure cap, and `jobFailure` still files it as `config`.
 */
function isTerminalPollFailure(error: unknown, signal: AbortSignal): boolean {
  if (signal.aborted) return true;
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 400 || status === 404;
}

/**
 * The status poll's own reading of a 404, and the ONLY place a download is classified `expired`:
 * this job id was accepted at enqueue, so a 404 for it now means the converter has dropped it. A
 * 404 at any other stage is not that — an enqueue names no job — so `jobFailure` never files a
 * bare 404 as `expired`. Anything else is returned untouched for `jobFailure` to classify.
 */
function classifyStatusPollFailure(error: unknown, jobId: string): unknown {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) return error;
  return new ImpactReportPdfDownloadError(
    { kind: "expired" },
    `status poll for job ${jobId} answered 404 (${error.config?.url ?? "unknown url"}): the job is unknown or past its window`,
    { cause: error },
  );
}

/**
 * Polls until the job is `completed` or `failed`. The download's own deadline bounds the loop;
 * `IMPACT_REPORT_PDF_POLL_MAX_CONSECUTIVE_FAILURES` bounds a run of failed polls, after which the
 * last error is rethrown for `jobFailure` to classify.
 */
async function pollUntilTerminal(jobId: string, signal: AbortSignal): Promise<PdfConversionJob> {
  let consecutiveFailures = 0;
  for (;;) {
    await sleep(IMPACT_REPORT_PDF_POLL_INTERVAL_MS, signal);
    try {
      const job = await fetchImpactReportPdfConversionStatus(jobId, signal);
      consecutiveFailures = 0;
      if (isTerminalJob(job)) return job;
    } catch (error) {
      consecutiveFailures += 1;
      if (
        isTerminalPollFailure(error, signal) ||
        consecutiveFailures >= IMPACT_REPORT_PDF_POLL_MAX_CONSECUTIVE_FAILURES
      ) {
        throw classifyStatusPollFailure(error, jobId);
      }
      console.warn("downloadImpactReportPdf: status poll failed, polling again", {
        consecutiveFailures,
        ...describeImpactReportError(error),
      });
    }
  }
}

/**
 * Builds the HTML. The serialiser is loaded on demand: it pulls `react-dom/server` and the hero
 * components along with it, which nothing else on the page needs until the user asks for a PDF.
 */
async function serialiseDocument(
  source: ImpactReportPdfDownloadSource,
  signal: AbortSignal,
): Promise<string> {
  try {
    const serialiser = await import("@/utils/impact-report-document-helper");
    return await serialiser.serialiseImpactReportDocument(source, signal);
  } catch (error) {
    if (signal.aborted) throw error;
    throw new ImpactReportPdfDownloadError(
      { kind: "serialisation" },
      `could not serialise the document: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/** Platform's flat `{ error: "<message>" }`, read off a failed response — never asserted. */
function platformErrorReason(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null || !("error" in body)) return undefined;
  const { error } = body;
  return typeof error === "string" && error ? error : undefined;
}

/**
 * Classifies whatever ended a download that was neither abandoned nor out of time. A 404 is NOT
 * read here: only the status poll knows a 404 means expired (`classifyStatusPollFailure`), so one
 * reaching this point came from another stage and is a converter failure like any other.
 */
function jobFailure(error: unknown): ImpactReportPdfDownloadFailure {
  if (error instanceof ImpactReportPdfDownloadError) return error.failure;
  if (!axios.isAxiosError(error)) return { kind: "converter" };

  const status = error.response?.status;
  if (status === 503) return { kind: "config" };
  // A 400's text is a DTO verdict ("html must be N bytes or fewer") written for a developer.
  const reason = status === 400 ? undefined : platformErrorReason(error.response?.data);
  return reason ? { kind: "converter", reason } : { kind: "converter" };
}

/**
 * The organization's own name, trimmed and clamped — the enqueue route's `filename` suggestion
 * and, with `.pdf`, the saved file's name. The server sanitises its own copy; an anchor's
 * `download` attribute is a DOM value, not a header, so this only has to be reasonable.
 */
function buildOrganizationFilenameStem(orgName: string): string | undefined {
  const trimmed = orgName.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, IMPACT_REPORT_PDF_SUGGESTED_FILENAME_MAX_LENGTH);
}

/**
 * THE one download function: serialises the report the caller is already showing, enqueues the
 * conversion, polls it to a terminal state, collects the bytes and saves them.
 *
 * A no-op while a download is pending. Both affordances disable themselves off the shared
 * snapshot, but the guard is repeated here so a double-click, or both affordances in one tick,
 * cannot start two jobs against a converter running at concurrency one.
 */
export async function downloadImpactReportPdf(
  source: ImpactReportPdfDownloadSource,
): Promise<void> {
  if (snapshot.status === "pending") return;

  const controller = new AbortController();
  activeController = controller;
  const { signal } = controller;
  let deadlineReached = false;
  const deadlineId = setTimeout(() => {
    deadlineReached = true;
    controller.abort();
  }, IMPACT_REPORT_PDF_DOWNLOAD_DEADLINE_MS);
  setSnapshot({ status: "pending", errorMessage: null });

  try {
    const html = await serialiseDocument(source, signal);
    const filenameStem = buildOrganizationFilenameStem(source.orgName);

    const enqueued = await enqueueImpactReportPdfConversion(html, filenameStem, signal);
    const terminal = isTerminalJob(enqueued)
      ? enqueued
      : await pollUntilTerminal(enqueued.jobId, signal);

    if (terminal.status === "failed") {
      throw new ImpactReportPdfDownloadError(
        terminal.error ? { kind: "converter", reason: terminal.error } : { kind: "converter" },
        `conversion job failed: ${terminal.error || "(no reason given)"}`,
      );
    }

    // Refuses anything that is not a whole PDF, so nothing short or foreign reaches `downloadBlob`.
    const blob = await fetchImpactReportPdfConversionContent(terminal.jobId, filenameStem, signal);
    downloadBlob(`${filenameStem ?? IMPACT_REPORT_PDF_FALLBACK_FILENAME_STEM}.pdf`, blob);
    setSnapshot({ status: "idle", errorMessage: null });
  } catch (error) {
    if (signal.aborted && !deadlineReached) {
      // Abandoned: every affordance unmounted. Back to idle so a return to `/impact` in the same
      // SPA session finds the controls usable rather than pending on a job nobody is watching.
      console.warn(
        "downloadImpactReportPdf: abandoned (every consumer unmounted)",
        describeImpactReportError(error),
      );
      setSnapshot({ status: "idle", errorMessage: null });
      return;
    }

    const failure: ImpactReportPdfDownloadFailure = deadlineReached
      ? { kind: "timeout" }
      : jobFailure(error);
    console.error("downloadImpactReportPdf: failed", {
      failure,
      ...describeImpactReportError(error),
    });
    setSnapshot({ status: "idle", errorMessage: resolveImpactReportPdfErrorMessage(failure) });
  } finally {
    clearTimeout(deadlineId);
    if (activeController === controller) activeController = null;
  }
}
